import { useEffect, useMemo, useState } from "react";
import { Check, Plus, X, Filter, BadgePercent, Search } from "lucide-react";
import { toast } from "sonner";
import {
  FUNDERS,
  CATEGORIES,
  PRICE_PER_FUNDER,
  DISCOUNT_THRESHOLD,
  DISCOUNT_PCT,
  MAX_SELECTION,
  computeQuote,
} from "@/data/funders";
import FlutterwavePaymentModal from "@/components/FlutterwavePaymentModal";

const CAT_COLORS = {
  Grant: "bg-moss/10 text-moss border-moss/20",
  DFI: "bg-umber/10 text-umber border-umber/20",
  VC: "bg-terracotta/10 text-terracotta border-terracotta/20",
  Accelerator: "bg-gold/15 text-umber border-gold/40",
};

export default function FunderMarketplace() {
  const [selected, setSelected] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [payOpen, setPayOpen] = useState(false);

  // Listen for "preset-funders" event from Pricing tier CTAs
  useEffect(() => {
    const handler = (e) => {
      const count = Math.min(Math.max(e?.detail?.count ?? 0, 0), MAX_SELECTION);
      if (count <= 0) return;
      // pre-select the first N funders from the canonical list
      setSelected(FUNDERS.slice(0, count).map((f) => f.id));
      toast.success(`${count} funder${count > 1 ? "s" : ""} pre-selected — adjust below.`);
    };
    window.addEventListener("seedtech:preset-funders", handler);
    return () => window.removeEventListener("seedtech:preset-funders", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FUNDERS.filter((f) => {
      const inCat = category === "All" || f.category === category;
      const inQ =
        !q ||
        f.name.toLowerCase().includes(q) ||
        f.focus.toLowerCase().includes(q) ||
        f.region.toLowerCase().includes(q);
      return inCat && inQ;
    });
  }, [category, query]);

  const quote = useMemo(() => computeQuote(selected.length), [selected]);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTION) {
        toast.error(`You can pick up to ${MAX_SELECTION} platforms in one engagement.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const clear = () => setSelected([]);

  const selectedFunders = FUNDERS.filter((f) => selected.includes(f.id));

  const onSubmit = () => {
    if (selectedFunders.length === 0) {
      toast.error("Add at least one funder to your bundle first.");
      return;
    }
    setPayOpen(true);
  };

  return (
    <section id="funders" className="relative bg-cream py-24 sm:py-32 bg-adinkra">
      <div className="mx-auto max-w-7xl px-6 sm:px-12">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-terracotta">Funder marketplace</p>
          <h2 className="mt-3 font-display text-4xl font-semibold text-umber sm:text-5xl">
            Pick the funders <span className="italic text-terracotta">you want to win.</span>
          </h2>
          <p className="mt-5 text-lg text-umber/70">
            We prepare your application to each platform for a flat <strong>${PRICE_PER_FUNDER.toLocaleString()}</strong> per funder.
            Select <strong>{DISCOUNT_THRESHOLD} or more</strong> and get an automatic <strong>{Math.round(DISCOUNT_PCT * 100)}% off</strong> the bundle. Up to {MAX_SELECTION} per engagement.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Filters + grid */}
          <div className="lg:col-span-8">
            {/* Search + tabs */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-umber/40" />
                <input
                  data-testid="funder-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search funders, focus, region…"
                  className="w-full rounded-md border border-border bg-white py-2.5 pl-9 pr-3 text-sm focus:border-umber focus:outline-none"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-umber/40" />
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`funder-filter-${c.toLowerCase()}`}
                    onClick={() => setCategory(c)}
                    className={`rounded-full border px-3 py-1 text-xs transition ${
                      category === c
                        ? "border-umber bg-umber text-cream"
                        : "border-border bg-white text-umber/70 hover:border-umber"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {filtered.map((f) => {
                const isSelected = selected.includes(f.id);
                const disabled = !isSelected && selected.length >= MAX_SELECTION;
                return (
                  <article
                    key={f.id}
                    data-testid={`funder-card-${f.id}`}
                    className={`group relative flex flex-col rounded-2xl border bg-white p-5 transition ${
                      isSelected
                        ? "border-terracotta shadow-lg shadow-terracotta/10"
                        : "border-border hover:-translate-y-0.5 hover:border-umber hover:shadow-md"
                    } ${disabled ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span
                          className={`mb-2 inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                            CAT_COLORS[f.category] || "bg-umber/10 text-umber border-umber/20"
                          }`}
                        >
                          {f.category}
                        </span>
                        <h3 className="font-display text-lg font-semibold leading-snug text-umber">{f.name}</h3>
                      </div>
                      <button
                        data-testid={`funder-toggle-${f.id}`}
                        onClick={() => !disabled && toggle(f.id)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition ${
                          isSelected
                            ? "bg-terracotta text-cream hover:bg-terracotta-dark"
                            : "border border-border bg-white text-umber hover:border-umber"
                        } disabled:cursor-not-allowed`}
                      >
                        {isSelected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </button>
                    </div>

                    <dl className="mt-4 space-y-1.5 text-xs text-umber/70">
                      <div className="flex gap-2">
                        <dt className="w-16 flex-shrink-0 uppercase tracking-wider text-umber/45">Focus</dt>
                        <dd className="flex-1">{f.focus}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="w-16 flex-shrink-0 uppercase tracking-wider text-umber/45">Ticket</dt>
                        <dd className="flex-1 font-medium text-umber">{f.ticket}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="w-16 flex-shrink-0 uppercase tracking-wider text-umber/45">Region</dt>
                        <dd className="flex-1">{f.region}</dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full rounded-md border border-dashed border-border bg-white/60 p-10 text-center text-sm text-umber/55">
                  No funders match that search. Try clearing filters.
                </div>
              )}
            </div>
          </div>

          {/* Summary sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-2xl border border-border bg-white p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-semibold text-umber">Your bundle</h3>
                {selected.length > 0 && (
                  <button
                    data-testid="funder-clear-button"
                    onClick={clear}
                    className="text-xs text-umber/55 hover:text-terracotta"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-umber/55">
                {selected.length} of {MAX_SELECTION} selected
              </p>

              {/* progress */}
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-sand">
                <div
                  className="h-full bg-terracotta transition-all"
                  style={{ width: `${(selected.length / MAX_SELECTION) * 100}%` }}
                />
              </div>

              {/* selected list */}
              <ul className="mt-5 max-h-64 space-y-2 overflow-y-auto chat-scroll">
                {selectedFunders.length === 0 ? (
                  <li className="rounded-md border border-dashed border-border bg-sand/40 p-4 text-center text-xs text-umber/55">
                    Tap any funder above to add it to your bundle.
                  </li>
                ) : (
                  selectedFunders.map((f) => (
                    <li
                      key={f.id}
                      data-testid={`bundle-item-${f.id}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-border bg-sand/40 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-umber">{f.name}</div>
                        <div className="truncate text-[10px] uppercase tracking-wider text-umber/50">
                          {f.category}
                        </div>
                      </div>
                      <button
                        data-testid={`bundle-remove-${f.id}`}
                        onClick={() => toggle(f.id)}
                        className="rounded-md p-1 text-umber/45 hover:bg-white hover:text-terracotta"
                        aria-label="Remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))
                )}
              </ul>

              {/* quote */}
              <dl className="mt-6 space-y-2 border-t border-border pt-4 text-sm">
                <div className="flex justify-between text-umber/70">
                  <dt>Subtotal</dt>
                  <dd data-testid="quote-subtotal" className="font-medium text-umber">
                    ${quote.subtotal.toLocaleString()}
                  </dd>
                </div>
                {quote.discountApplies ? (
                  <div className="flex items-center justify-between rounded-md bg-moss/10 px-2 py-1.5 text-moss">
                    <dt className="inline-flex items-center gap-1 text-xs">
                      <BadgePercent className="h-3.5 w-3.5" />
                      Multi-funder discount ({Math.round(DISCOUNT_PCT * 100)}%)
                    </dt>
                    <dd data-testid="quote-discount" className="text-sm font-medium">
                      -${quote.discountAmount.toLocaleString()}
                    </dd>
                  </div>
                ) : (
                  <p className="text-[11px] text-umber/55">
                    Pick {DISCOUNT_THRESHOLD - selected.length > 0 ? DISCOUNT_THRESHOLD - selected.length : 0} more to
                    unlock a {Math.round(DISCOUNT_PCT * 100)}% bundle discount.
                  </p>
                )}
                <div className="flex items-end justify-between border-t border-border pt-3">
                  <dt className="text-xs uppercase tracking-wider text-umber/55">Total</dt>
                  <dd data-testid="quote-total" className="font-display text-3xl font-semibold text-umber">
                    ${quote.total.toLocaleString()}
                  </dd>
                </div>
              </dl>

              <button
                data-testid="bundle-submit"
                onClick={onSubmit}
                disabled={selected.length === 0}
                className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-medium transition ${
                  selected.length === 0
                    ? "cursor-not-allowed bg-umber/30 text-cream"
                    : "bg-terracotta text-cream hover:-translate-y-0.5 hover:bg-terracotta-dark"
                }`}
              >
                {selected.length === 0
                  ? "Select funders to continue"
                  : `Pay for ${selected.length} funder${selected.length > 1 ? "s" : ""}`}
              </button>
              <p className="mt-3 text-center text-[11px] text-umber/50">
                Secure checkout via Flutterwave · USD converted to NGN live.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <FlutterwavePaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        selectedFunders={selectedFunders}
        quote={quote}
      />
    </section>
  );
}
